import { CityState, Building } from './types';

export class UtilityEngine {
  /**
   * Recalculates power and water grid propagation from source utilities (solar/coal, towers/pumps) across the city.
   * Roads act as transmission lines/pipes, carrying utilities with low range drop-off.
   */
  public tickUtilities(state: CityState) {
    // 1. Reset grids
    state.powerGrid = {};
    state.waterGrid = {};

    const powerSources: Building[] = [];
    const waterSources: Building[] = [];

    // 2. Locate active and completed utility plants
    for (const key of Object.keys(state.buildings)) {
      const b = state.buildings[key];
      if (b.stage === 'completed' && b.zone === 'civic') {
        if (b.familyId === 'power_solar' || b.familyId === 'power_coal') {
          powerSources.push(b);
        } else if (b.familyId === 'water_tower' || b.familyId === 'water_pump') {
          waterSources.push(b);
        }
      }
    }

    // 3. Run Breadth-First Search (BFS) propagation
    this.propagateUtility(state, powerSources, 'power');
    this.propagateUtility(state, waterSources, 'water');

    // 4. Update utility flags on each individual building in the city
    for (const key of Object.keys(state.buildings)) {
      const b = state.buildings[key];
      const keyCell = `${b.x},${b.y}`;

      // Utility buildings themselves are self-sustained
      if (b.zone === 'civic' && (b.familyId.includes('power') || b.familyId.includes('water'))) {
        b.isPowered = true;
        b.isWatered = true;
        continue;
      }

      b.isPowered = !!state.powerGrid[keyCell];
      b.isWatered = !!state.waterGrid[keyCell];
    }
  }

  private propagateUtility(state: CityState, sources: Building[], type: 'power' | 'water') {
    const queue: { x: number; y: number; range: number }[] = [];
    const visited = new Set<string>();

    const grid = type === 'power' ? state.powerGrid : state.waterGrid;

    // Load active source nodes into the BFS queue with their unique ranges
    sources.forEach((s) => {
      let range = 10; // Default solar range
      if (s.familyId === 'power_coal') range = 18;
      else if (s.familyId === 'water_tower') range = 12;
      else if (s.familyId === 'water_pump') range = 20;

      const key = `${s.x},${s.y}`;
      queue.push({ x: s.x, y: s.y, range });
      visited.add(key);
      grid[key] = true;
    });

    const directions = [
      { x: 0, y: -1 }, // North
      { x: 1, y: 0 },  // East
      { x: 0, y: 1 },  // South
      { x: -1, y: 0 }  // West
    ];

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (current.range <= 0) continue;

      for (const dir of directions) {
        const nx = current.x + dir.x;
        const ny = current.y + dir.y;

        if (nx < 0 || nx >= state.width || ny < 0 || ny >= state.height) continue;

        const nkey = `${nx},${ny}`;
        if (visited.has(nkey)) continue;

        // Roads act as excellent conduits (pipes/lines embedded).
        // Passing through paved roads costs 1 range. Raw terrain costs 2.
        const isRoad = !!state.roads[nkey];
        const rangeCost = isRoad ? 1 : 2;

        const nextRange = current.range - rangeCost;
        if (nextRange >= 0) {
          visited.add(nkey);
          grid[nkey] = true;
          queue.push({ x: nx, y: ny, range: nextRange });
        }
      }
    }
  }
}
